// DataCloudChunkedUploader.ts - Optimized chunked upload service for Data Cloud
import { getApiUrl } from '@/config/environment';
import {
  getAuthToken,
  createSocketManager,
  calculateBackoff,
  validateCSVSecurity,
  validateCSVFormat,
  translateMessage,
  CHUNK_SIZES,
  type SocketManager
} from '@/core/services/upload/utils';
import type { DecimalPrecision } from '@/types/common';

/**
 * Extract structured error fields from a backend error response body.
 *
 * The backend sends `code` and `suggestion`; older/alternate responses may
 * provide a `suggestions[]` array, which we treat as a backward-compat fallback.
 */
export function extractBackendError(body: Record<string, unknown>): { code?: string; suggestion?: string } {
  // Backend uses `error_code` on the quota/auth gates and `code` on domain errors.
  // Accept either; `code` takes precedence when both are present.
  const code =
    typeof body?.code === 'string'
      ? body.code
      : typeof body?.error_code === 'string'
        ? body.error_code
        : undefined;
  let suggestion: string | undefined;
  if (typeof body?.suggestion === 'string') suggestion = body.suggestion;
  else if (Array.isArray(body?.suggestions) && typeof body.suggestions[0] === 'string') suggestion = body.suggestions[0];
  return { code, suggestion };
}

/**
 * Interface for upload progress tracking
 * Dva odvojena progress bara:
 * - uploadProgress: Frontend kontrolira (chunk upload) 0-100%
 * - processingProgress: Backend kontrolira (CSV obrada) 0-100%
 */
export interface DataCloudUploadProgress {
  progress?: number;             // Legacy - za backward compatibility
  uploadProgress?: number;       // 0-100% - Frontend chunk upload
  processingProgress?: number;   // 0-100% - Backend processing
  status: 'uploading' | 'processing' | 'completed' | 'error';
  phase?: 'file_upload' | 'data_processing' | 'regression_analysis' | 'interpolation';
  message?: string;
  step?: string;
  detail?: string;
  error?: string;
  eta?: number;                  // Estimated time remaining in seconds
  etaFormatted?: string;         // Formatted ETA string (e.g., "2m 30s")
}

/**
 * Interface for chunk metadata
 */
export interface DataCloudChunkMetadata {
  uploadId: string;
  chunkIndex: number;
  totalChunks: number;
  chunkSize: number;
  totalSize: number;
  fileType: 'temp_file' | 'load_file' | 'interpolate_file';
}

/**
 * Interface for regression parameters
 */
export interface RegressionParams {
  REG: 'lin' | 'poly';
  TR: 'cnt' | 'dep';
  TOL_CNT?: string;
  TOL_DEP?: string;
  TOL_DEP_EXTRA?: string;
  decimalPrecision?: DecimalPrecision;
}

/**
 * Interface for a single regression data point from NDJSON stream
 */
export interface RegressionStreamPoint {
  x: number;
  y: number;
  predicted: number;
  upperBound: number;
  lowerBound: number;
  inside: boolean;
}

/**
 * Interface for regression streaming result metadata
 */
export interface RegressionStreamResult {
  equation: string;
  totalRows: number;
  removedPoints: number;
}

/**
 * Interface for interpolation parameters
 */
export interface InterpolationParams {
  max_time_span: number;
  ensure_numeric?: boolean;
  /** Decimal precision applied to interpolated values ('full' or integer) */
  decimalPrecision?: DecimalPrecision;
}

/**
 * Interface for server response
 */
export interface DataCloudServerResponse {
  success?: boolean;
  error?: string;
  message?: string;
  data?: {
    x_values?: number[];
    y_values?: number[];
    predicted_y?: number[];
    upper_bound?: number[];
    lower_bound?: number[];
    removed_points?: number;
    headers?: {
      temp_file?: string[];
      load_file?: string[];
    };
  };
  streaming?: boolean;
}

/**
 * Optimized class for chunked upload of Data Cloud files
 * Implements Socket.IO for real-time progress tracking and retry logic
 */
export class DataCloudChunkedUploader {
  // Configuration constants
  private static readonly CHUNK_SIZE = CHUNK_SIZES.MEDIUM; // 5MB chunks
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_BASE_DELAY = 1000;
  private static readonly REQUEST_TIMEOUT = 300000; // 5 minutes

  private static get API_BASE_URL(): string {
    return import.meta.env.VITE_API_URL?.replace(/\/+$/, '') || '';
  }

  // Socket manager instance
  private static socketManager: SocketManager | null = null;
  // Progress tracking to prevent regression
  private static highestProgress: number = 0;

  /**
   * Get or create socket manager instance
   */
  private static getSocketManager(): SocketManager {
    if (!this.socketManager) {
      this.socketManager = createSocketManager({
        timeout: this.REQUEST_TIMEOUT,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
    }
    return this.socketManager;
  }

  /**
   * Close Socket.IO connection and cleanup
   */
  public static disconnectSocket(uploadId?: string): void {
    if (this.socketManager) {
      this.socketManager.disconnect(uploadId);
      this.socketManager = null;
    }
  }

  /**
   * Upload a single chunk with retry logic
   */
  private static async uploadChunk(
    chunk: Blob,
    metadata: DataCloudChunkMetadata,
    maxRetries: number = this.MAX_RETRIES
  ): Promise<Response> {
    const formData = new FormData();

    // Add chunk as blob
    formData.append("file", chunk);

    // Add chunk metadata
    formData.append("uploadId", metadata.uploadId);
    formData.append("chunkIndex", metadata.chunkIndex.toString());
    formData.append("totalChunks", metadata.totalChunks.toString());
    formData.append("fileType", metadata.fileType);

    // Get authentication token
    const accessToken = await getAuthToken();

    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);

      try {
        const response = await fetch(`${this.API_BASE_URL}/api/cloud/upload-chunk`, {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
            'Authorization': `Bearer ${accessToken}`
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorMsg = `Chunk upload failed: ${response.status} ${response.statusText}`;
          let errorCode: string | undefined;
          let suggestion: string | undefined;
          try {
            const body = await response.clone().json();
            if (typeof body?.error === 'string') errorMsg = body.error;
            ({ code: errorCode, suggestion } = extractBackendError(body));
          } catch {
            /* non-JSON body — use status-line fallback */
          }
          const err = new Error(errorMsg) as Error & { code?: string; status?: number; suggestion?: string };
          err.status = response.status;
          if (errorCode) err.code = errorCode;
          if (suggestion) err.suggestion = suggestion;
          throw err;
        }

        return response;
      } catch (error: unknown) {
        clearTimeout(timeoutId);
        lastError = error instanceof Error ? error : new Error('Unknown error');

        if (error instanceof Error && error.name === 'AbortError') {
          lastError = new Error('Request timeout - server not responding');
        }

        // Do NOT retry 4xx client errors (e.g. 413 payload too large,
        // 429 rate limited). The backend's answer won't change on
        // replay, so retrying only burns ~30s of backoff while hiding
        // the real error from the user.
        const status = (error as { status?: number } | undefined)?.status;
        if (status !== undefined && status >= 400 && status < 500) {
          throw lastError;
        }

        if (attempt === maxRetries) {
          throw lastError;
        }

        // Wait before retry (exponential backoff)
        const delay = calculateBackoff(attempt, this.RETRY_BASE_DELAY);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError || new Error('Max retries exceeded');
  }

  /**
   * Upload file in chunks with progress tracking
   */
  public static async uploadFile(
    file: File,
    fileType: 'temp_file' | 'load_file' | 'interpolate_file',
    uploadId: string,
    onProgress?: (progress: DataCloudUploadProgress) => void,
    onUploadComplete?: () => Promise<void>
  ): Promise<void> {
    // Reset progress tracking for new upload
    this.highestProgress = 0;

    // CSV Security and Format validation for CSV files
    if (file.name.toLowerCase().endsWith('.csv')) {
      const fileContent = await file.text();

      // Security validation - check for injection attacks
      const securityResult = validateCSVSecurity(fileContent);
      if (!securityResult.isValid) {
        throw new Error(`Security validation failed: ${securityResult.error}`);
      }

      // Format validation - check column consistency
      const formatResult = validateCSVFormat(fileContent, { minColumns: 2, minLines: 2 });
      if (!formatResult.isValid) {
        throw new Error(`CSV format validation failed: ${formatResult.error}`);
      }
    }

    // Initialize Socket.IO connection
    const socketManager = this.getSocketManager();

    // Join upload room
    socketManager.joinRoom(uploadId);

    // Setup progress listener for backend processing updates
    const progressHandler = (data: Record<string, unknown>) => {
      if (data.uploadId === uploadId && onProgress) {
        const rawProgress = Math.min(Math.max((data.progress as number) || 0, 0), 100);

        // Prevent progress regression - only allow progress to go up
        if (rawProgress < this.highestProgress) {
          return;
        }

        if (rawProgress > this.highestProgress) {
          this.highestProgress = rawProgress;
        }

        // Translate messageKey to localized message
        const translatedMessage = data.messageKey
          ? translateMessage(
              data.messageKey as string,
              data.messageParams as Record<string, unknown> | undefined,
              data.message as string | undefined,
              'dataCloud.processingMessages'
            )
          : String(data.message || '');

        onProgress({
          uploadProgress: 100,
          processingProgress: rawProgress,
          status: 'processing',
          phase: (data.phase as DataCloudUploadProgress['phase']) || 'file_upload',
          message: translatedMessage,
          step: data.step as string,
          detail: data.detail as string,
          eta: data.eta ? Number(data.eta) : undefined,
          etaFormatted: data.etaFormatted ? String(data.etaFormatted) : undefined
        });
      }
    };

    socketManager.onProgress(progressHandler);

    try {
      const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);

      if (totalChunks === 0) {
        throw new Error('Empty file');
      }

      // Progress callback for start
      onProgress?.({
        uploadProgress: 0,
        processingProgress: 0,
        status: 'uploading',
        phase: 'file_upload',
        message: 'Starting file upload...'
      });

      // Upload chunks
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * this.CHUNK_SIZE;
        const end = Math.min(start + this.CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const metadata: DataCloudChunkMetadata = {
          uploadId,
          chunkIndex,
          totalChunks,
          chunkSize: chunk.size,
          totalSize: file.size,
          fileType
        };

        await this.uploadChunk(chunk, metadata);

        // Update progress (0-100% for upload)
        const uploadProgress = ((chunkIndex + 1) / totalChunks) * 100;
        onProgress?.({
          uploadProgress,
          processingProgress: 0,
          status: 'uploading',
          phase: 'file_upload',
          message: `Uploading chunk ${chunkIndex + 1}/${totalChunks} (${uploadProgress.toFixed(0)}%)`
        });
      }

      // Upload completed
      onProgress?.({
        uploadProgress: 100,
        processingProgress: 0,
        status: 'completed',
        phase: 'file_upload',
        message: 'File upload completed successfully'
      });

      // Refresh usage data after successful upload
      if (onUploadComplete) {
        try {
          await onUploadComplete();
        } catch {
          // Don't throw - upload succeeded even if refresh failed
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      onProgress?.({
        uploadProgress: 0,
        processingProgress: 0,
        status: 'error',
        phase: 'file_upload',
        error: errorMessage
      });

      throw error;
    } finally {
      // Cleanup Socket.IO listener
      socketManager.offProgress(progressHandler);
    }
  }

  /**
   * Process regression analysis
   */
  public static async processRegression(
    uploadId: string,
    params: RegressionParams,
    onProgress?: (progress: DataCloudUploadProgress) => void,
    onData?: (data: RegressionStreamPoint[]) => void,
    onUploadComplete?: () => Promise<void>
  ): Promise<RegressionStreamResult> {
    // Initialize Socket.IO and join room
    const socketManager = this.getSocketManager();
    socketManager.joinRoom(uploadId);

    // Setup progress listener for backend processing
    const progressHandler = (data: Record<string, unknown>) => {
      if (data.uploadId === uploadId && onProgress) {
        const rawProgress = Math.min(Math.max((data.progress as number) || 0, 0), 100);

        // Translate messageKey to localized message
        const translatedMessage = data.messageKey
          ? translateMessage(
              data.messageKey as string,
              data.messageParams as Record<string, unknown> | undefined,
              data.message as string | undefined,
              'dataCloud.processingMessages'
            )
          : String(data.message || '');

        onProgress({
          uploadProgress: 100,
          processingProgress: rawProgress,
          status: 'processing',
          phase: (data.phase as DataCloudUploadProgress['phase']) || 'regression_analysis',
          message: translatedMessage,
          step: data.step as string,
          detail: data.detail as string,
          eta: data.eta ? Number(data.eta) : undefined,
          etaFormatted: data.etaFormatted ? String(data.etaFormatted) : undefined
        });
      }
    };

    socketManager.onProgress(progressHandler);

    try {
      const accessToken = await getAuthToken();

      onProgress?.({
        uploadProgress: 100,
        processingProgress: 0,
        status: 'processing',
        phase: 'regression_analysis',
        message: 'Starting regression analysis...'
      });

      const response = await fetch(getApiUrl('/api/cloud/complete'), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/x-ndjson",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          uploadId,
          ...params
        }),
      });

      if (!response.ok) {
        let errorMsg = `Regression processing failed: ${response.status} ${response.statusText}`;
        let errorCode: string | undefined;
        let suggestion: string | undefined;
        try {
          const body = await response.clone().json();
          if (typeof body?.error === 'string') errorMsg = body.error;
          else if (typeof body?.data?.error === 'string') errorMsg = body.data.error;
          ({ code: errorCode, suggestion } = extractBackendError(body));
        } catch {
          /* non-JSON body — use status-line fallback */
        }
        const err = new Error(errorMsg) as Error & { code?: string; status?: number; suggestion?: string };
        err.status = response.status;
        if (errorCode) err.code = errorCode;
        if (suggestion) err.suggestion = suggestion;
        throw err;
      }

      // Process NDJSON stream
      const reader = response.body?.getReader();
      if (!reader) throw new Error("Stream reader could not be created");

      let equation = '';
      let totalRows = 0;
      let removedPoints = 0;
      let totalChunks = 0;
      let receivedChunks = 0;
      let buffer = '';
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Process remaining buffer
          if (buffer.trim()) {
            try {
              processLine(JSON.parse(buffer.trim()));
            } catch { /* ignore incomplete final line */ }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          // Skip proxy debug messages
          if (trimmed.includes('<Response streamed')) continue;

          if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            try {
              processLine(JSON.parse(trimmed));
            } catch {
              // Skip malformed lines
            }
          }
        }
      }

      reader.releaseLock();

      function processLine(chunk: Record<string, unknown>) {
        if (chunk.type === 'meta') {
          totalRows = (chunk.total_rows as number) || 0;
          totalChunks = (chunk.total_chunks as number) || 0;
          equation = (chunk.equation as string) || '';
        } else if (chunk.type === 'data') {
          receivedChunks++;
          const chunkData = chunk.data as RegressionStreamPoint[];
          if (chunkData && Array.isArray(chunkData)) {
            onData?.(chunkData);
          }
          // Progress: map streaming progress to 75-100%
          const progress = Math.round(75 + (receivedChunks / Math.max(totalChunks, 1)) * 25);
          onProgress?.({
            uploadProgress: 100,
            processingProgress: Math.min(progress, 99),
            status: 'processing',
            phase: 'regression_analysis',
            message: `Streaming data ${receivedChunks}/${totalChunks}`
          });
        } else if (chunk.type === 'complete') {
          removedPoints = (chunk.removed_points as number) || 0;
          onProgress?.({
            uploadProgress: 100,
            processingProgress: 100,
            status: 'completed',
            phase: 'regression_analysis',
            message: 'Regression analysis completed'
          });
        }
      }

      // Refresh usage data after successful regression
      if (onUploadComplete) {
        try {
          await onUploadComplete();
        } catch {
          // Don't throw - regression succeeded even if refresh failed
        }
      }

      return { equation, totalRows, removedPoints };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      onProgress?.({
        uploadProgress: 100,
        processingProgress: 0,
        status: 'error',
        phase: 'regression_analysis',
        error: errorMessage
      });

      throw error;
    } finally {
      // Cleanup Socket.IO listener
      socketManager.offProgress(progressHandler);
      socketManager.leaveRoom(uploadId);
    }
  }

  /**
   * Process interpolation with streaming support
   */
  public static async processInterpolation(
    uploadId: string,
    params: InterpolationParams,
    onProgress?: (progress: DataCloudUploadProgress) => void,
    onData?: (data: unknown[]) => void,
    onUploadComplete?: () => Promise<void>
  ): Promise<{ addedPoints: number; totalPoints: number; removedPoints: number }> {
    // Initialize Socket.IO and join room
    const socketManager = this.getSocketManager();
    socketManager.joinRoom(uploadId);

    // Setup progress listener for backend processing
    const progressHandler = (data: Record<string, unknown>) => {
      if (data.uploadId === uploadId && onProgress) {
        const rawProgress = Math.min(Math.max((data.progress as number) || 0, 0), 100);

        // Translate messageKey to localized message
        const translatedMessage = data.messageKey
          ? translateMessage(
              data.messageKey as string,
              data.messageParams as Record<string, unknown> | undefined,
              data.message as string | undefined,
              'dataCloud.processingMessages'
            )
          : String(data.message || '');

        onProgress({
          uploadProgress: 100,
          processingProgress: rawProgress,
          status: 'processing',
          phase: (data.phase as DataCloudUploadProgress['phase']) || 'interpolation',
          message: translatedMessage,
          step: data.step as string,
          detail: data.detail as string,
          eta: data.eta ? Number(data.eta) : undefined,
          etaFormatted: data.etaFormatted ? String(data.etaFormatted) : undefined
        });
      }
    };

    socketManager.onProgress(progressHandler);

    try {
      const accessToken = await getAuthToken();

      onProgress?.({
        uploadProgress: 100,
        processingProgress: 0,
        status: 'processing',
        phase: 'interpolation',
        message: 'Starting interpolation...'
      });

      const response = await fetch(getApiUrl('/api/cloud/interpolate-chunked'), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/x-ndjson",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          uploadId,
          ...params
        }),
      });

      if (!response.ok) {
        let errorMsg = `Interpolation failed: ${response.status} ${response.statusText}`;
        let errorCode: string | undefined;
        let suggestion: string | undefined;
        try {
          const body = await response.clone().json();
          if (typeof body?.error === 'string') errorMsg = body.error;
          ({ code: errorCode, suggestion } = extractBackendError(body));
        } catch {
          /* non-JSON body — use status-line fallback */
        }
        const err = new Error(errorMsg) as Error & { code?: string; status?: number; suggestion?: string };
        err.status = response.status;
        if (errorCode) err.code = errorCode;
        if (suggestion) err.suggestion = suggestion;
        throw err;
      }

      // Process the streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Stream reader could not be created");
      }

      const allPoints: unknown[] = [];
      let metaReceived = false;
      let totalChunks = 0;
      let receivedChunks = 0;
      let addedPoints = 0;
      let totalPoints = 0;
      const removedPoints = 0;

      // Process the stream
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Process any remaining data in the buffer
          if (buffer.trim()) {
            try {
              const chunk = JSON.parse(buffer.trim());
              processChunk(chunk);
            } catch {
              // Ignore parse errors
            }
          }
          break;
        }

        // Convert the Uint8Array to a string and add to buffer
        const textChunk = new TextDecoder().decode(value);
        buffer += textChunk;

        // Split by newlines and process each complete JSON object
        const lines = buffer.split("\n");

        // Keep the last line in the buffer as it might be incomplete
        buffer = lines.pop() || "";

        // Process complete lines
        for (const line of lines) {
          if (line.trim()) {
            try {
              const chunk = JSON.parse(line.trim());
              processChunk(chunk);
            } catch {
              // Ignore parse errors
            }
          }
        }
      }

      // Process each chunk of data
      function processChunk(chunk: Record<string, unknown>) {
        if (chunk.type === "meta") {
          metaReceived = true;
          totalChunks = chunk.total_chunks as number;
          addedPoints = chunk.added_points as number;
          totalPoints = chunk.total_rows as number;
        } else if (chunk.type === "data") {
          receivedChunks++;

          if (chunk.data && Array.isArray(chunk.data)) {
            allPoints.push(...chunk.data);
            onData?.(chunk.data);
          }

          // Update progress - map chunk progress (0-100%) to 90-100% range
          // So progress continues from 90% (cloud_starting_stream) to 100% during streaming
          const chunkProgress = metaReceived && totalChunks > 0
            ? (receivedChunks / totalChunks) * 100
            : 0;
          // Map 0-100% chunk progress to 90-100% overall progress
          const processingProgress = Math.round(90 + (chunkProgress / 10));

          onProgress?.({
            uploadProgress: 100,
            processingProgress,
            status: 'processing',
            phase: 'interpolation',
            message: `Processing chunk ${receivedChunks}/${totalChunks}`
          });
        } else if (chunk.type === "complete") {
          onProgress?.({
            uploadProgress: 100,
            processingProgress: 100,
            status: 'completed',
            phase: 'interpolation',
            message: 'Interpolation completed successfully'
          });
        }
      }

      // Refresh usage data after successful interpolation
      if (onUploadComplete) {
        try {
          await onUploadComplete();
        } catch {
          // Don't throw - interpolation succeeded even if refresh failed
        }
      }

      return {
        addedPoints,
        totalPoints,
        removedPoints
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      onProgress?.({
        uploadProgress: 100,
        processingProgress: 0,
        status: 'error',
        phase: 'interpolation',
        error: errorMessage
      });

      throw error;
    } finally {
      // Cleanup Socket.IO listener
      socketManager.offProgress(progressHandler);
      socketManager.leaveRoom(uploadId);
    }
  }
}

export default DataCloudChunkedUploader;
